import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getInvestorPackage } from '../../api/investorApi';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { StatusPill } from '../../components/ui/StatusPill';
import { formatDateTime } from '../../utils/format';
import { ActivityTimeline } from './ActivityTimeline';
import { InvestorFileViewer } from './InvestorFileViewer';
import { humanize } from './investorLabels';

function Fact({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-eorig-muted">{label}</dt>
      <dd className="mt-0.5 text-sm text-eorig-ink">{children}</dd>
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <section className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-eorig-ink">
        {title}
        {typeof count === 'number' ? (
          <span className="rounded-full bg-eorig-surface px-2 py-0.5 text-[11px] font-semibold text-eorig-muted">
            {count}
          </span>
        ) : null}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function formatBytes(value) {
  if (value == null) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

export function InvestorPackageDetailPage() {
  const { packageId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [viewingFileId, setViewingFileId] = useState('');

  useEffect(() => {
    setViewingFileId('');
  }, [packageId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await getInvestorPackage(packageId);
        if (!cancelled) setDetail(response);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load this package.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [packageId, reloadToken]);

  if (error) {
    return <ErrorBanner message={error} onRetry={() => setReloadToken((v) => v + 1)} />;
  }
  if (loading || !detail) {
    return <p className="text-sm text-eorig-muted">Loading package…</p>;
  }

  const pkg = detail.package;
  const viewingFile = detail.files.find((file) => file.id === viewingFileId) || null;

  return (
    <div className="space-y-4">
      <Link
        to="/investor/packages"
        className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
      >
        ← All packages
      </Link>

      <section className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-eorig-ink">
              {pkg.reference_number || pkg.package_id}
            </h2>
            <p className="text-xs text-eorig-muted">
              Created {formatDateTime(pkg.created_at)} · last change {formatDateTime(pkg.updated_at)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatusPill value={pkg.package_status} label={humanize(pkg.package_status)} />
            <StatusPill value={pkg.authority_status} label={`Authority: ${humanize(pkg.authority_status)}`} />
            <StatusPill value={pkg.custody_status} label={`Custody: ${humanize(pkg.custody_status)}`} />
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Fact label="Originator">{pkg.originator}</Fact>
          <Fact label="Program">{pkg.program}</Fact>
          <Fact label="Custodian">{pkg.custodian || '—'}</Fact>
          <Fact label="Upload">{humanize(detail.upload_status)}</Fact>
          <Fact label="Assets">{pkg.items_count}</Fact>
          <Fact label="Open exceptions">{detail.open_exceptions}</Fact>
          <Fact label="Evidence">
            {detail.evidence_total > 0
              ? `${detail.evidence_satisfied} / ${detail.evidence_total} satisfied`
              : '—'}
          </Fact>
        </dl>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Assets" count={detail.assets.length}>
          {detail.assets.length === 0 ? (
            <p className="text-xs text-eorig-muted">No assets recorded.</p>
          ) : (
            <ul className="divide-y divide-eorig-border text-sm">
              {detail.assets.map((asset) => (
                <li key={asset.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <div>
                    <span className="font-semibold text-eorig-ink">{asset.loan_number || asset.asset_number}</span>
                    <span className="block text-[11px] text-eorig-muted">{asset.borrower || '—'}</span>
                  </div>
                  <div className="flex gap-2">
                    <StatusPill value={asset.authority_state} label={humanize(asset.authority_state)} />
                    <StatusPill value={asset.custody_state} label={humanize(asset.custody_state)} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Files" count={detail.files.length}>
          {detail.files.length === 0 ? (
            <p className="text-xs text-eorig-muted">No files uploaded.</p>
          ) : (
            <ul className="divide-y divide-eorig-border text-sm">
              {detail.files.map((file) => (
                <li key={file.id} className="py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="min-w-0 break-all font-semibold text-eorig-ink">{file.file_name}</span>
                    <div className="flex items-center gap-2">
                      <StatusPill value={file.upload_status} label={humanize(file.upload_status)} />
                      {file.upload_status === 'COMPLETE' ? (
                        <button
                          type="button"
                          onClick={() => setViewingFileId(file.id === viewingFileId ? '' : file.id)}
                          aria-pressed={file.id === viewingFileId}
                          className="rounded-full border border-eorig-border bg-white px-2.5 py-1 text-[11px] font-semibold text-eorig-navy-600 hover:border-eorig-cyan hover:text-eorig-cyan"
                        >
                          {file.id === viewingFileId ? 'Hide' : 'View'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-[11px] text-eorig-muted">
                    {file.document_role ? `${humanize(file.document_role)} · ` : ''}
                    {formatBytes(file.size_bytes)}
                    {file.uploaded_at ? ` · ${formatDateTime(file.uploaded_at)}` : ''}
                  </p>
                  {file.content_sha256 ? (
                    <p className="truncate font-mono text-[11px] text-eorig-muted" title={file.content_sha256}>
                      sha256 {file.content_sha256}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {viewingFile ? (
        <InvestorFileViewer
          packageId={pkg.package_id}
          file={viewingFile}
          onClose={() => setViewingFileId('')}
        />
      ) : null}

      <Section title="Exceptions" count={detail.exceptions.length}>
        {detail.exceptions.length === 0 ? (
          <p className="text-xs text-eorig-muted">No exceptions raised.</p>
        ) : (
          <ul className="divide-y divide-eorig-border text-sm">
            {detail.exceptions.map((exception) => (
              <li key={exception.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div>
                  <span className="font-semibold text-eorig-ink">{exception.code}</span>
                  <span className="block text-[11px] text-eorig-muted">
                    {exception.description || '—'} · {formatDateTime(exception.created_at)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <StatusPill value={exception.severity} />
                  <StatusPill value={exception.status} label={humanize(exception.status)} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Timeline" count={detail.activity.length}>
        <ActivityTimeline items={detail.activity} emptyMessage="No activity recorded for this package." />
      </Section>
    </div>
  );
}
